import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth.tsx';
import { supabase } from '../lib/db.ts';
import { useSettings } from '../lib/settings/settingsContext.tsx';
import { 
  CheckCircle, 
  XCircle, 
  Ban, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  ExternalLink,
  AlertCircle,
  Loader2,
  Info,
  Key,
  Activity
} from 'lucide-react';
import toast from 'react-hot-toast';

interface DeveloperAccount {
  id: string;
  user_id: string;
  user_email: string;
  company_name: string;
  website: string | null;
  description: string;
  use_case: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  approved_by: string | null;
  metadata: Record<string, unknown>;
  api_key_count: number;
  commission_rate: number | null;
}

interface ApiKeyWithStats {
  id: string;
  key_name: string;
  environment: 'test' | 'production';
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  total_requests: number;
  requests_last_30_days: number;
  last_request_at: string | null;
}

interface UsageStats {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  avg_response_time_ms: number;
  total_data_transferred_bytes: number;
  requests_by_endpoint: Array<{
    endpoint: string;
    count: number;
    avg_response_time_ms: number;
  }>;
  requests_by_day: Array<{
    date: string;
    count: number;
  }>;
}

export function AdminDeveloperManagement() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [accounts, setAccounts] = useState<DeveloperAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState<DeveloperAccount | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ accountId: string; action: string } | null>(null);
  const [editingCommissionRate, setEditingCommissionRate] = useState<string | null>(null);
  const [commissionRateValue, setCommissionRateValue] = useState<string>('');
  const [updatingCommission, setUpdatingCommission] = useState(false);
  const [showApiKeysModal, setShowApiKeysModal] = useState<string | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKeyWithStats[]>([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    if (user) {
      fetchAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentPage, statusFilter]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      
      // Prepare parameters - ensure types match function signature
      const params: {
        p_status_filter: string | null;
        p_page: number;
        p_page_size: number;
      } = {
        p_status_filter: statusFilter === 'all' ? null : statusFilter,
        p_page: currentPage,
        p_page_size: pageSize
      };
      
      console.log('Calling admin_list_developer_accounts with params:', params);
      
      const { data, error } = await supabase.rpc('admin_list_developer_accounts', params);

      console.log('fetchAccounts response:', { data, error, statusFilter });

      if (error) {
        console.error('RPC error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      if (data) {
        if (data.success) {
          console.log('Accounts fetched:', data.accounts?.length || 0, 'accounts');
          console.log('Total count:', data.total_count);
          console.log('Status filter:', statusFilter);
          setAccounts(data.accounts || []);
          setTotalCount(data.total_count || 0);
          setTotalPages(data.total_pages || 1);
        } else {
          console.error('Function returned error:', data.error);
          throw new Error(data.error || 'Failed to fetch developer accounts');
        }
      } else {
        console.error('No data returned from function');
        throw new Error('No data returned from server');
      }
    } catch (err) {
      console.error('Error fetching developer accounts:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load developer accounts');
      setAccounts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (accountId: string, action: 'approve' | 'reject' | 'suspend', reason?: string) => {
    try {
      setActionLoading(accountId);
      
      const { data, error } = await supabase.rpc('admin_manage_developer_account', {
        p_developer_account_id: accountId,
        p_action: action,
        p_reason: reason || null
      });

      if (error) throw error;

      if (data && data.success) {
        toast.success(`Developer account ${action}d successfully`);
        fetchAccounts();
        setShowRejectionModal(false);
        setRejectionReason('');
        setPendingAction(null);
      } else {
        throw new Error(data?.error || `Failed to ${action} developer account`);
      }
    } catch (err) {
      console.error(`Error ${action}ing developer account:`, err);
      toast.error(err instanceof Error ? err.message : `Failed to ${action} developer account`);
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectionModal = (accountId: string, action: 'reject' | 'suspend') => {
    setPendingAction({ accountId, action });
    setShowRejectionModal(true);
  };

  const submitRejection = () => {
    if (pendingAction) {
      handleAction(pendingAction.accountId, pendingAction.action as 'reject' | 'suspend', rejectionReason);
    }
  };

  const viewAccountDetails = async (accountId: string) => {
    try {
      const { data, error } = await supabase.rpc('admin_get_developer_account', {
        p_developer_account_id: accountId
      });

      if (error) throw error;

      if (data && data.success) {
        setSelectedAccount(data.account);
        setShowDetailsModal(true);
      } else {
        throw new Error(data?.error || 'Failed to fetch account details');
      }
    } catch (err) {
      console.error('Error fetching account details:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load account details');
    }
  };

  const viewApiKeys = async (accountId: string) => {
    try {
      setLoadingApiKeys(true);
      setShowApiKeysModal(accountId);
      
      // Use the admin_get_developer_account function which already includes API keys
      const { data: accountData, error: accountError } = await supabase.rpc('admin_get_developer_account', {
        p_developer_account_id: accountId
      });

      if (accountError) throw accountError;
      if (!accountData || !accountData.success) {
        throw new Error(accountData?.error || 'Failed to fetch developer account');
      }

      let keysData = accountData.account?.api_keys || [];

      // If no keys found via developer_account_id, try to find keys via created_by
      if (!keysData || keysData.length === 0) {
        const { data: devAccount, error: devError } = await supabase
          .from('developer_accounts')
          .select('user_id')
          .eq('id', accountId)
          .single();

        if (devAccount && !devError) {
          const { data: keysByUser, error: keysError } = await supabase
            .from('api_keys')
            .select('id, key_name, environment, is_active, created_at, last_used_at')
            .eq('created_by', devAccount.user_id)
            .order('created_at', { ascending: false });

          if (!keysError && keysByUser && keysByUser.length > 0) {
            keysData = keysByUser.map((key: { id: string; key_name: string; environment: string; is_active: boolean; created_at: string; last_used_at: string | null }) => ({
              id: key.id,
              key_name: key.key_name,
              environment: key.environment,
              is_active: key.is_active,
              created_at: key.created_at,
              last_used_at: key.last_used_at
            }));
          }
        }
      }

      // Get usage stats for each key
      const keysWithStats = await Promise.all(
        (keysData || []).map(async (key: { id: string; key_name: string; environment: string; is_active: boolean; created_at: string; last_used_at: string | null }) => {
          const { data: statsData } = await supabase
            .from('api_key_usage')
            .select('id, created_at, status_code')
            .eq('api_key_id', key.id);

          const totalRequests = statsData?.length || 0;
          const requestsLast30Days = statsData?.filter(
            (s: { created_at: string }) => new Date(s.created_at) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          ).length || 0;
          const lastRequestAt = statsData && statsData.length > 0
            ? statsData.reduce((latest, current) =>
                new Date(current.created_at) > new Date(latest.created_at) ? current : latest
              ).created_at
            : null;

          return {
            ...key,
            total_requests: totalRequests,
            requests_last_30_days: requestsLast30Days,
            last_request_at: lastRequestAt
          };
        })
      );

      setApiKeys(keysWithStats);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoadingApiKeys(false);
    }
  };

  const fetchUsageStats = async (apiKeyId: string) => {
    try {
      setLoadingStats(true);
      const { data, error } = await supabase.rpc('get_api_key_usage_stats', {
        p_api_key_id: apiKeyId,
        p_start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        p_end_date: new Date().toISOString()
      });

      if (error) throw error;

      if (data && data.success) {
        setUsageStats(data.stats);
      } else {
        throw new Error(data?.error || 'Failed to fetch usage statistics');
      }
    } catch (err) {
      console.error('Error fetching usage stats:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load usage statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleUpdateCommissionRate = async (accountId: string) => {
    try {
      setUpdatingCommission(true);
      
      // Convert percentage to decimal (e.g., 5% -> 0.05)
      const commissionRate = parseFloat(commissionRateValue);
      
      if (isNaN(commissionRate) || commissionRate < 0 || commissionRate > 100) {
        toast.error('Commission rate must be a number between 0 and 100');
        return;
      }
      
      const commissionRateDecimal = commissionRate / 100;
      
      const { data, error } = await supabase.rpc('admin_update_developer_commission_rate', {
        p_developer_account_id: accountId,
        p_commission_rate: commissionRateDecimal
      });

      if (error) throw error;

      if (data && data.success) {
        toast.success('Commission rate updated successfully');
        setEditingCommissionRate(null);
        setCommissionRateValue('');
        fetchAccounts();
        // Update selected account if modal is open
        if (selectedAccount && selectedAccount.id === accountId) {
          setSelectedAccount({
            ...selectedAccount,
            commission_rate: commissionRateDecimal
          });
        }
      } else {
        throw new Error(data?.error || 'Failed to update commission rate');
      }
    } catch (err) {
      console.error('Error updating commission rate:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update commission rate');
    } finally {
      setUpdatingCommission(false);
    }
  };

  const filteredAccounts = accounts.filter(account => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      account.company_name?.toLowerCase().includes(search) ||
      account.user_email?.toLowerCase().includes(search) ||
      account.description?.toLowerCase().includes(search)
    );
  });

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      suspended: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status as keyof typeof styles] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">Please log in to access the admin panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Developer Account Management</h1>
          <p className="text-gray-600">Review and manage developer account applications</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by company, email, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total Accounts</div>
            <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Pending</div>
            <div className="text-2xl font-bold text-yellow-600">
              {accounts.filter(a => a.status === 'pending').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Approved</div>
            <div className="text-2xl font-bold text-green-600">
              {accounts.filter(a => a.status === 'approved').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Rejected/Suspended</div>
            <div className="text-2xl font-bold text-red-600">
              {accounts.filter(a => a.status === 'rejected' || a.status === 'suspended').length}
            </div>
          </div>
        </div>

        {/* Accounts Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-wayja-teal" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No developer accounts found</p>
              <p className="text-sm text-gray-500">
                {statusFilter === 'all' 
                  ? 'No developer accounts have been registered yet.'
                  : `No developer accounts with status "${statusFilter}" found.`}
              </p>
              {statusFilter !== 'all' && (
                <button
                  onClick={() => setStatusFilter('all')}
                  className="mt-4 text-wayja-teal hover:text-wayja-dark-teal underline"
                >
                  Show all accounts
                </button>
              )}
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No accounts match your search criteria</p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 text-wayja-teal hover:text-wayja-dark-teal underline"
              >
                Clear search
              </button>
            </div>
          ) : (
            <>
              <div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        API Keys
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1">
                          Commission Rate
                          <div className="group relative">
                            <Info className="w-4 h-4 text-gray-400 cursor-help" />
                            <div className="absolute left-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                              <p className="font-semibold mb-1">How Commission Works:</p>
                              <p className="mb-2">The commission rate is a percentage of the service fee (currently {(settings.serviceFee * 100).toFixed(1)}% of each bet).</p>
                              <p className="mb-1">Example: 50% commission = {(50 * settings.serviceFee).toFixed(2)}% of each bet amount</p>
                              <p className="text-gray-300 text-xs">(50% of {(settings.serviceFee * 100).toFixed(2)}% service fee = {(50 * settings.serviceFee).toFixed(2)}% of bet)</p>
                              <p className="mt-2 text-gray-400 text-xs italic">Service fee is configurable in System Settings</p>
                            </div>
                          </div>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAccounts.map((account) => (
                      <tr key={account.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{account.company_name || 'N/A'}</div>
                          {account.website && (
                            <a
                              href={account.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-wayja-teal hover:underline flex items-center gap-1"
                            >
                              {account.website}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{account.user_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(account.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={() => viewApiKeys(account.id)}
                            className="text-wayja-teal hover:text-wayja-dark-teal hover:underline flex items-center gap-1"
                          >
                            <Key className="w-4 h-4" />
                            {account.api_key_count}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {editingCommissionRate === account.id ? (
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={commissionRateValue}
                                  onChange={(e) => setCommissionRateValue(e.target.value)}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                  placeholder="0.00"
                                />
                                <span className="text-gray-500">%</span>
                                <button
                                  onClick={() => handleUpdateCommissionRate(account.id)}
                                  disabled={updatingCommission}
                                  className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                  title="Save"
                                >
                                  {updatingCommission ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingCommissionRate(null);
                                    setCommissionRateValue('');
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                  title="Cancel"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="text-xs text-gray-500">
                                {commissionRateValue && !isNaN(parseFloat(commissionRateValue)) ? (
                                  <span>
                                    = {(parseFloat(commissionRateValue) * settings.serviceFee).toFixed(4)}% of each bet amount
                                  </span>
                                ) : (
                                  <span className="text-gray-400">Percentage of {(settings.serviceFee).toFixed(1)}% service fee</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span>
                                {account.commission_rate !== null && account.commission_rate !== undefined
                                  ? `${(account.commission_rate * 100).toFixed(2)}%`
                                  : 'Not set'}
                              </span>
                              {account.status === 'approved' && (
                                <button
                                  onClick={() => {
                                    setEditingCommissionRate(account.id);
                                    setCommissionRateValue(
                                      account.commission_rate !== null && account.commission_rate !== undefined
                                        ? (account.commission_rate * 100).toString()
                                        : ''
                                    );
                                  }}
                                  className="text-wayja-teal hover:text-wayja-dark-teal text-xs"
                                  title="Edit Commission Rate"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(account.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => viewAccountDetails(account.id)}
                              className="text-wayja-teal hover:text-wayja-dark-teal"
                              title="View Details"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            {account.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleAction(account.id, 'approve')}
                                  disabled={actionLoading === account.id}
                                  className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                  title="Approve"
                                >
                                  {actionLoading === account.id ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-5 h-5" />
                                  )}
                                </button>
                                <button
                                  onClick={() => openRejectionModal(account.id, 'reject')}
                                  disabled={actionLoading === account.id}
                                  className="text-red-600 hover:text-red-700 disabled:opacity-50"
                                  title="Reject"
                                >
                                  <XCircle className="w-5 h-5" />
                                </button>
                              </>
                            )}
                            {account.status === 'approved' && (
                              <button
                                onClick={() => openRejectionModal(account.id, 'suspend')}
                                disabled={actionLoading === account.id}
                                className="text-orange-600 hover:text-orange-700 disabled:opacity-50"
                                title="Suspend"
                              >
                                {actionLoading === account.id ? (
                                  <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                  <Ban className="w-5 h-5" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Showing page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Rejection/Suspension Modal */}
      {showRejectionModal && pendingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {pendingAction.action === 'reject' ? 'Reject Developer Account' : 'Suspend Developer Account'}
            </h3>
            <p className="text-gray-600 mb-4">
              {pendingAction.action === 'reject' 
                ? 'Please provide a reason for rejecting this developer account (optional but recommended):'
                : 'Please provide a reason for suspending this developer account (optional but recommended):'}
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter reason..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 min-h-[100px]"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectionReason('');
                  setPendingAction(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitRejection}
                disabled={actionLoading !== null}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : (pendingAction.action === 'reject' ? 'Reject' : 'Suspend')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">Developer Account Details</h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedAccount(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Company Name</label>
                <div className="mt-1 text-gray-900">{selectedAccount.company_name || 'N/A'}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <div className="mt-1 text-gray-900">{selectedAccount.user_email}</div>
              </div>
              {selectedAccount.website && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Website</label>
                  <div className="mt-1">
                    <a
                      href={selectedAccount.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-wayja-teal hover:underline"
                    >
                      {selectedAccount.website}
                    </a>
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <div className="mt-1 text-gray-900">{selectedAccount.description}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Use Case</label>
                <div className="mt-1 text-gray-900">{selectedAccount.use_case}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1">{getStatusBadge(selectedAccount.status)}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Created</label>
                <div className="mt-1 text-gray-900">
                  {new Date(selectedAccount.created_at).toLocaleString()}
                </div>
              </div>
              {selectedAccount.approved_at && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Approved</label>
                  <div className="mt-1 text-gray-900">
                    {new Date(selectedAccount.approved_at).toLocaleString()}
                  </div>
                </div>
              )}
              {selectedAccount.status === 'approved' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Commission Rate
                    <div className="group relative">
                      <Info className="w-4 h-4 text-gray-400 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 shadow-lg">
                        <p className="font-semibold mb-2">How Developer Commission Works:</p>
                        <ul className="space-y-1 text-gray-200">
                          <li>• Commission is calculated from the <strong>{(settings.serviceFee).toFixed(1)}% service fee</strong> on each bet</li>
                          <li>• The rate you set is a percentage of that service fee</li>
                          <li>• Example: 50% commission rate = {(50 * settings.serviceFee).toFixed(2)}% of each bet amount</li>
                          <li>• (50% of {(settings.serviceFee).toFixed(1)}% service fee = {(50 * settings.serviceFee).toFixed(2)}% of the bet)</li>
                        </ul>
                        <p className="mt-2 text-gray-300 text-xs">
                          Commission applies to all bets placed on markets created by this developer.
                        </p>
                        <p className="mt-1 text-gray-400 text-xs italic">
                          Service fee is configurable in System Settings
                        </p>
                      </div>
                    </div>
                  </label>
                  <div className="mt-1">
                    {editingCommissionRate === selectedAccount.id ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={commissionRateValue}
                            onChange={(e) => setCommissionRateValue(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-32"
                            placeholder="0.00"
                          />
                          <span className="text-gray-500">%</span>
                          <button
                            onClick={() => handleUpdateCommissionRate(selectedAccount.id)}
                            disabled={updatingCommission}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                          >
                            {updatingCommission ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => {
                              setEditingCommissionRate(null);
                              setCommissionRateValue('');
                            }}
                            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                        {commissionRateValue && !isNaN(parseFloat(commissionRateValue)) && (
                          <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
                            <strong>Calculation:</strong> {commissionRateValue}% of service fee = <strong>{(parseFloat(commissionRateValue) * settings.serviceFee).toFixed(2)}%</strong> of each bet amount
                            <br />
                            <span className="text-gray-500">(Service fee is {(settings.serviceFee * 100).toFixed(2)}% of each bet - configurable in System Settings)</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900">
                            {selectedAccount.commission_rate !== null && selectedAccount.commission_rate !== undefined
                              ? `${(selectedAccount.commission_rate * 100).toFixed(2)}%`
                              : 'Not set'}
                          </span>
                          <button
                            onClick={() => {
                              setEditingCommissionRate(selectedAccount.id);
                              setCommissionRateValue(
                                selectedAccount.commission_rate !== null && selectedAccount.commission_rate !== undefined
                                  ? (selectedAccount.commission_rate * 100).toString()
                                  : ''
                              );
                            }}
                            className="text-wayja-teal hover:text-wayja-dark-teal text-sm underline"
                          >
                            Edit
                          </button>
                        </div>
                        {selectedAccount.commission_rate !== null && selectedAccount.commission_rate !== undefined && (
                          <div className="text-xs text-gray-500">
                            = {(selectedAccount.commission_rate * settings.serviceFee * 100).toFixed(4)}% of each bet amount
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* API Keys and Usage Stats Modal */}
      {showApiKeysModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">API Keys & Usage Statistics</h3>
              <button
                onClick={() => {
                  setShowApiKeysModal(null);
                  setApiKeys([]);
                  setSelectedApiKeyId(null);
                  setUsageStats(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {loadingApiKeys ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-wayja-teal" />
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-12">
                <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No API keys found for this developer.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.map((key) => (
                  <div key={key.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-lg font-semibold text-gray-900">{key.key_name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            key.environment === 'production' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {key.environment}
                          </span>
                          {key.is_active ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-gray-500">Total Requests</div>
                            <div className="font-semibold text-gray-900">{key.total_requests.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Last 30 Days</div>
                            <div className="font-semibold text-gray-900">{key.requests_last_30_days.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Created</div>
                            <div className="font-semibold text-gray-900">{formatDate(key.created_at)}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Last Used</div>
                            <div className="font-semibold text-gray-900">{formatDate(key.last_request_at)}</div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (selectedApiKeyId === key.id) {
                            setSelectedApiKeyId(null);
                            setUsageStats(null);
                          } else {
                            setSelectedApiKeyId(key.id);
                            fetchUsageStats(key.id);
                          }
                        }}
                        className={`ml-4 p-2 rounded-lg ${
                          selectedApiKeyId === key.id
                            ? 'bg-wayja-teal text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title="View Detailed Statistics"
                      >
                        <Activity className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Detailed Usage Stats */}
                    {selectedApiKeyId === key.id && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {loadingStats ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-wayja-teal" />
                          </div>
                        ) : usageStats ? (
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-3">Detailed Statistics (Last 30 Days)</h5>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                              <div className="bg-blue-50 rounded-lg p-3">
                                <div className="text-xs text-blue-600 font-medium mb-1">Total Requests</div>
                                <div className="text-xl font-bold text-blue-900">
                                  {usageStats.total_requests?.toLocaleString() || 0}
                                </div>
                              </div>
                              <div className="bg-green-50 rounded-lg p-3">
                                <div className="text-xs text-green-600 font-medium mb-1">Successful</div>
                                <div className="text-xl font-bold text-green-900">
                                  {usageStats.successful_requests?.toLocaleString() || 0}
                                </div>
                              </div>
                              <div className="bg-red-50 rounded-lg p-3">
                                <div className="text-xs text-red-600 font-medium mb-1">Failed</div>
                                <div className="text-xl font-bold text-red-900">
                                  {usageStats.failed_requests?.toLocaleString() || 0}
                                </div>
                              </div>
                              <div className="bg-purple-50 rounded-lg p-3">
                                <div className="text-xs text-purple-600 font-medium mb-1">Avg Response</div>
                                <div className="text-xl font-bold text-purple-900">
                                  {usageStats.avg_response_time_ms 
                                    ? `${Math.round(usageStats.avg_response_time_ms)}ms`
                                    : 'N/A'}
                                </div>
                              </div>
                            </div>
                            {usageStats.requests_by_endpoint && usageStats.requests_by_endpoint.length > 0 && (
                              <div className="mb-4">
                                <h6 className="font-medium text-gray-700 mb-2">Requests by Endpoint</h6>
                                <div className="space-y-2">
                                  {usageStats.requests_by_endpoint.map((endpoint, idx) => (
                                    <div key={idx} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                                      <span className="font-mono text-gray-700">{endpoint.endpoint}</span>
                                      <div className="flex items-center gap-4">
                                        <span className="text-gray-600">{endpoint.count} requests</span>
                                        <span className="text-gray-500">
                                          {endpoint.avg_response_time_ms 
                                            ? `${Math.round(endpoint.avg_response_time_ms)}ms avg`
                                            : ''}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {usageStats.total_data_transferred_bytes && usageStats.total_data_transferred_bytes > 0 && (
                              <div className="text-sm text-gray-600">
                                <strong>Total Data Transferred:</strong> {formatBytes(usageStats.total_data_transferred_bytes)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            No usage statistics available for this API key.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

